import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAffiliateUsageLimits } from '@/hooks/useAffiliateUsageLimits';
import { useAffiliateInfo } from '@/hooks/useAffiliate';
import { AffiliateDefaultLimitsSettings } from './AffiliateDefaultLimitsSettings';
import { toast } from 'sonner';
import { Users, Settings, Plus, Trash2, Crown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

// Compact mobile-optimized user card component
const CompactMobileUserCard = ({ 
  user, 
  isEditing, 
  editingUser, 
  setEditingUser, 
  handleSetUserLimits, 
  handleRemoveUserLimits, 
  handleConvertToCustom, 
  isUpdatingLimits, 
  isRemovingLimits 
}: any) => (
  <Card className="p-3 space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm truncate">{user.userName}</h3>
        <p className="text-xs text-muted-foreground truncate">{user.userEmail}</p>
        <div className="flex items-center gap-1 mt-1">
          <Badge variant={user.isCustom ? "default" : "secondary"} className="text-xs py-0.5 px-1.5">
            {user.isCustom ? (
              <><Crown className="h-2.5 w-2.5 mr-1" />Custom</>
            ) : (
              'Default'
            )}
          </Badge>
        </div>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs text-muted-foreground">Daily</Label>
        {isEditing ? (
          <Input
            type="number"
            min="0"
            value={editingUser.daily}
            onChange={(e) => setEditingUser({
              ...editingUser,
              daily: parseInt(e.target.value) || 0
            })}
            className="mt-0.5 h-7 text-xs"
          />
        ) : (
          <div className="mt-0.5">
            <Badge variant="outline" className="text-xs py-0.5">{user.daily}</Badge>
          </div>
        )}
      </div>
      
      <div>
        <Label className="text-xs text-muted-foreground">Monthly</Label>
        {isEditing ? (
          <Input
            type="number"
            min="0"
            value={editingUser.monthly}
            onChange={(e) => setEditingUser({
              ...editingUser,
              monthly: parseInt(e.target.value) || 0
            })}
            className="mt-0.5 h-7 text-xs"
          />
        ) : (
          <div className="mt-0.5">
            <Badge variant="outline" className="text-xs py-0.5">{user.monthly}</Badge>
          </div>
        )}
      </div>
    </div>
    
    <div className="flex gap-2 pt-1">
      {isEditing ? (
        <>
          <Button
            size="sm"
            onClick={() => handleSetUserLimits(
              user.userId,
              editingUser.daily,
              editingUser.monthly
            )}
            disabled={isUpdatingLimits}
            className="flex-1 h-7 text-xs"
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditingUser(null)}
            className="flex-1 h-7 text-xs"
          >
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => user.isCustom ? 
              setEditingUser({
                userId: user.userId,
                daily: user.daily,
                monthly: user.monthly
              }) : 
              handleConvertToCustom(user)
            }
            className="flex-1 h-7 text-xs"
          >
            <Settings className="h-2.5 w-2.5 mr-1" />
            Edit
          </Button>
          {user.isCustom && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleRemoveUserLimits(user.userId)}
              disabled={isRemovingLimits}
              title="Reset to default limits"
              className="h-7 px-2"
            >
              <Trash2 className="h-2.5 w-2.5" />
            </Button>
          )}
        </>
      )}
    </div>
  </Card>
);

export const AffiliateUserLimitsManagement = () => {
  const isMobile = useIsMobile();
  const { data: affiliateInfo } = useAffiliateInfo();
  const { 
    limitsOverview,
    affiliateDefaults,
    totalLimits,
    allocatedLimits,
    availableToAllocate,
    userAllocations,
    isLoading,
    setUserLimits,
    removeUserLimits,
    setAffiliateDefaults,
    applyDefaultsToAll,
    canAllocate,
    getAllocationPercentage,
    isUpdatingLimits,
    isRemovingLimits,
    isUpdatingDefaults,
    isApplyingDefaults
  } = useAffiliateUsageLimits(affiliateInfo?.id || '');

  const [editingUser, setEditingUser] = useState<{
    userId: string;
    daily: number;
    monthly: number;
  } | null>(null);

  const [newUserLimits, setNewUserLimits] = useState({
    userId: '',
    daily: affiliateDefaults?.daily || 3,
    monthly: affiliateDefaults?.monthly || 20
  });

  const allocationPercentage = getAllocationPercentage();

  const handleSetUserLimits = async (userId: string, daily: number, monthly: number) => {
    const currentUser = userAllocations.find(u => u.userId === userId);
    const currentDaily = currentUser?.daily || 0;
    const currentMonthly = currentUser?.monthly || 0;
    
    const dailyDifference = daily - currentDaily;
    const monthlyDifference = monthly - currentMonthly;
    
    if (dailyDifference > availableToAllocate.daily || monthlyDifference > availableToAllocate.monthly) {
      toast.error('Not enough available quota to allocate these limits');
      return;
    }

    setUserLimits({ userId, daily, monthly });
    setEditingUser(null);
  };

  const handleRemoveUserLimits = async (userId: string) => {
    removeUserLimits(userId);
  };

  const handleConvertToCustom = (user: any) => {
    setEditingUser({
      userId: user.userId,
      daily: user.daily,
      monthly: user.monthly
    });
  };

  const handleSetDefaults = (daily: number, monthly: number) => {
    setAffiliateDefaults({ daily, monthly });
  };

  const handleApplyToAll = (daily: number, monthly: number) => {
    applyDefaultsToAll({ daily, monthly });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
          <div className={`${isMobile ? 'h-24' : 'h-32'} bg-muted rounded`}></div>
        </div>
      </div>
    );
  }

  if (!affiliateInfo?.id) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className={`text-center text-muted-foreground ${isMobile ? 'text-sm' : ''}`}>
            You need to be an active affiliate to manage user limits.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-${isMobile ? '3' : '4'}`}>
      {/* Default Limits Settings */}
      <AffiliateDefaultLimitsSettings
        affiliateDefaults={affiliateDefaults}
        availableToAllocate={availableToAllocate}
        userCount={userAllocations.length}
        isUpdatingDefaults={isUpdatingDefaults}
        isApplyingDefaults={isApplyingDefaults}
        onSetDefaults={handleSetDefaults}
        onApplyToAll={handleApplyToAll}
      />

      {/* Compact Quota Overview */}
      <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-2 gap-4'}`}>
        <Card>
          <CardHeader className={`${isMobile ? 'pb-2 px-3 pt-3' : 'pb-3'}`}>
            <CardTitle className={`${isMobile ? 'text-sm' : 'text-lg'}`}>Daily Quota</CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'px-3 pb-3' : ''}>
            <div className="space-y-2">
              <div className={`flex justify-between ${isMobile ? 'text-xs' : 'text-sm'}`}>
                <span>Allocated</span>
                <span>{allocatedLimits.daily} / {totalLimits.daily}</span>
              </div>
              <Progress value={allocationPercentage.daily} className="h-1.5" />
              <div className={`flex justify-between text-xs text-muted-foreground`}>
                <span>Available: {availableToAllocate.daily}</span>
                <span>{allocationPercentage.daily}% used</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className={`${isMobile ? 'pb-2 px-3 pt-3' : 'pb-3'}`}>
            <CardTitle className={`${isMobile ? 'text-sm' : 'text-lg'}`}>Monthly Quota</CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'px-3 pb-3' : ''}>
            <div className="space-y-2">
              <div className={`flex justify-between ${isMobile ? 'text-xs' : 'text-sm'}`}>
                <span>Allocated</span>
                <span>{allocatedLimits.monthly} / {totalLimits.monthly}</span>
              </div>
              <Progress value={allocationPercentage.monthly} className="h-1.5" />
              <div className={`flex justify-between text-xs text-muted-foreground`}>
                <span>Available: {availableToAllocate.monthly}</span>
                <span>{allocationPercentage.monthly}% used</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Limits Management */}
      <Card>
        <CardHeader className={isMobile ? 'px-3 pt-3 pb-2' : ''}>
          <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between items-center'}`}>
            <CardTitle className={isMobile ? 'text-sm' : ''}>Referred Users & Limits</CardTitle>
            <Dialog>
              <DialogTrigger asChild>
                <Button size={isMobile ? "sm" : "default"} className={isMobile ? 'w-full h-8 text-xs' : ''}>
                  <Plus className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                  Add User Limits
                </Button>
              </DialogTrigger>
              <DialogContent className={isMobile ? 'w-[95vw] max-w-sm' : ''}>
                <DialogHeader>
                  <DialogTitle className={isMobile ? 'text-sm' : ''}>Set Custom User Limits</DialogTitle>
                  <DialogDescription className={isMobile ? 'text-xs' : ''}>
                    Allocate specific daily and monthly limits for a user.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="userId" className={isMobile ? 'text-xs' : ''}>User ID</Label>
                    <Input
                      id="userId"
                      placeholder="Enter user ID"
                      value={newUserLimits.userId}
                      onChange={(e) => setNewUserLimits({
                        ...newUserLimits,
                        userId: e.target.value
                      })}
                      className={isMobile ? 'h-8 text-sm mt-1' : ''}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="daily" className={isMobile ? 'text-xs' : ''}>Daily Limit</Label>
                      <Input
                        id="daily"
                        type="number"
                        min="0"
                        value={newUserLimits.daily}
                        onChange={(e) => setNewUserLimits({
                          ...newUserLimits,
                          daily: parseInt(e.target.value) || 0
                        })}
                        className={isMobile ? 'h-8 text-sm mt-1' : ''}
                      />
                    </div>
                    <div>
                      <Label htmlFor="monthly" className={isMobile ? 'text-xs' : ''}>Monthly Limit</Label>
                      <Input
                        id="monthly"
                        type="number"
                        min="0"
                        value={newUserLimits.monthly}
                        onChange={(e) => setNewUserLimits({
                          ...newUserLimits,
                          monthly: parseInt(e.target.value) || 0
                        })}
                        className={isMobile ? 'h-8 text-sm mt-1' : ''}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      handleSetUserLimits(newUserLimits.userId, newUserLimits.daily, newUserLimits.monthly);
                      setNewUserLimits({ userId: '', daily: affiliateDefaults?.daily || 3, monthly: affiliateDefaults?.monthly || 20 });
                    }}
                    disabled={!newUserLimits.userId || isUpdatingLimits}
                    className={`w-full ${isMobile ? 'h-8 text-xs' : ''}`}
                  >
                    Set Custom Limits
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className={isMobile ? 'px-3 pb-3' : ''}>
          {/* Mobile Compact Card Layout */}
          {isMobile ? (
            <div className="space-y-3">
              {userAllocations.length > 0 ? (
                userAllocations.map((user) => {
                  const isEditing = editingUser?.userId === user.userId;
                  
                  return (
                    <CompactMobileUserCard
                      key={user.userId}
                      user={user}
                      isEditing={isEditing}
                      editingUser={editingUser}
                      setEditingUser={setEditingUser}
                      handleSetUserLimits={handleSetUserLimits}
                      handleRemoveUserLimits={handleRemoveUserLimits}
                      handleConvertToCustom={handleConvertToCustom}
                      isUpdatingLimits={isUpdatingLimits}
                      isRemovingLimits={isRemovingLimits}
                    />
                  );
                })
              ) : (
                <Card className="border-dashed border-2 border-muted">
                  <CardContent className="flex flex-col items-center justify-center py-6 text-center">
                    <Users className="h-6 w-6 text-muted-foreground mb-2" />
                    <p className="text-xs text-muted-foreground">
                      No referred users found. Users will appear here automatically when they sign up via your referral link.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            /* Desktop Table Layout */
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">User</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Daily</TableHead>
                    <TableHead className="text-xs">Monthly</TableHead>
                    <TableHead className="text-xs">Updated</TableHead>
                    <TableHead className="text-right text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userAllocations.length > 0 ? (
                    userAllocations.map((user) => {
                      const isEditing = editingUser?.userId === user.userId;
                      
                      return (
                        <TableRow key={user.userId}>
                          <TableCell>
                            <div>
                              <div className="font-medium text-sm">{user.userName}</div>
                              <div className="text-xs text-muted-foreground">{user.userEmail}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.isCustom ? "default" : "secondary"} className="text-xs">
                              {user.isCustom ? (
                                <><Crown className="h-2.5 w-2.5 mr-1" />Custom</>
                              ) : (
                                'Default'
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Input
                                type="number"
                                min="0"
                                value={editingUser.daily}
                                onChange={(e) => setEditingUser({
                                  ...editingUser,
                                  daily: parseInt(e.target.value) || 0
                                })}
                                className="w-16 h-7 text-xs"
                              />
                            ) : (
                              <Badge variant="outline" className="text-xs">{user.daily}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Input
                                type="number"
                                min="0"
                                value={editingUser.monthly}
                                onChange={(e) => setEditingUser({
                                  ...editingUser,
                                  monthly: parseInt(e.target.value) || 0
                                })}
                                className="w-16 h-7 text-xs"
                              />
                            ) : (
                              <Badge variant="outline" className="text-xs">{user.monthly}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {user.lastUsed ? new Date(user.lastUsed).toLocaleDateString() : 
                               user.isCustom ? 'Custom set' : 'Auto default'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {isEditing ? (
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  onClick={() => handleSetUserLimits(
                                    user.userId,
                                    editingUser.daily,
                                    editingUser.monthly
                                  )}
                                  disabled={isUpdatingLimits}
                                  className="h-7 text-xs"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingUser(null)}
                                  className="h-7 text-xs"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => user.isCustom ? 
                                    setEditingUser({
                                      userId: user.userId,
                                      daily: user.daily,
                                      monthly: user.monthly
                                    }) : 
                                    handleConvertToCustom(user)
                                  }
                                  className="h-7 px-2"
                                >
                                  <Settings className="h-3 w-3" />
                                </Button>
                                {user.isCustom && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRemoveUserLimits(user.userId)}
                                    disabled={isRemovingLimits}
                                    title="Reset to default limits"
                                    className="h-7 px-2"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-xs">
                        No referred users found. Users will appear here automatically when they sign up via your referral link.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
